import { Component, Watch } from 'vue-property-decorator';
import EditorBase from '../EditorBase';
import { Tabs, TextInput, Toggle, StatusSwitch, Selector, ColorPicker } from 'streamlabs-beaker';
import { IComponent, IComponentCss, TFontStyle, TTextAlign } from '@/helpers/editor';
import FontHelper from '@/helpers/font';
import Position from './Position.vue';
import GeneralSettings from './GeneralSettings.vue';
import VisibilityDeleteComponentButton from './VisibilityDeleteComponentButton.vue';
import EditorApi from '@/apis/editor-api';

interface ITextSettingsTab {
  name: string;
  value: string;
}

interface IFormattedFontWeight {
  id: string;
  label: string;
  fontWeight: number;
  fontStyle?: string;
}

const DEFAULT_TEXT_SETTINGS = {
  textValue: '',
  color: '#4F5E65',
  fontSize: 32,
  fontFamily: 'Roboto',
  fontWeight: 400,
  fontStyle: 'normal' as TFontStyle,
  lineHeight: 'normal',
  letterSpacing: 'normal',
  textAlign: 'left' as TTextAlign,
  textDecoration: null,
  textShadowEnabled: false,
  textShadowColor: '#000000',
  textShadowOffset: '0px 0px 0px',
  textStrokeEnabled: false,
  textStrokeColor: '#000000',
  textStrokeWidth: 0,
  tickerEnabled: false,
  backgroundColor: '#000000',
};

@Component({
  components: {
    STabs: Tabs,
    STextInput: TextInput,
    SToggle: Toggle,
    SStatusSwitch: StatusSwitch,
    SColorPicker: ColorPicker,
    SSelector: Selector,
    Position,
    GeneralSettings,
    VisibilityDeleteComponentButton,
  },
})
export default class SharedTextSettings extends EditorBase {
  textValue: string = DEFAULT_TEXT_SETTINGS.textValue;
  color: string = DEFAULT_TEXT_SETTINGS.color;
  fontSize: number | string = DEFAULT_TEXT_SETTINGS.fontSize;
  fontFamily: string = DEFAULT_TEXT_SETTINGS.fontFamily;
  fontWeight: number = DEFAULT_TEXT_SETTINGS.fontWeight;
  fontStyle: TFontStyle = DEFAULT_TEXT_SETTINGS.fontStyle;
  lineHeight: string = DEFAULT_TEXT_SETTINGS.lineHeight;
  letterSpacing: string = DEFAULT_TEXT_SETTINGS.letterSpacing;
  textAlign: TTextAlign = DEFAULT_TEXT_SETTINGS.textAlign;
  textDecoration: string = DEFAULT_TEXT_SETTINGS.textDecoration;
  textShadowEnabled: boolean = DEFAULT_TEXT_SETTINGS.textShadowEnabled;
  textShadowColor: string = DEFAULT_TEXT_SETTINGS.textShadowColor;
  textShadowOffset: string = DEFAULT_TEXT_SETTINGS.textShadowOffset;
  textStrokeEnabled: boolean = DEFAULT_TEXT_SETTINGS.textStrokeEnabled;
  textStrokeColor: string = DEFAULT_TEXT_SETTINGS.textStrokeColor;
  textStrokeWidth: number = DEFAULT_TEXT_SETTINGS.textStrokeWidth;

  tickerEnabled: boolean = DEFAULT_TEXT_SETTINGS.tickerEnabled;
  backgroundColor: string = DEFAULT_TEXT_SETTINGS.backgroundColor;

  shouldTriggerSave: boolean = false;

  async mounted() {
    if (!this.availableFonts) {
      const response = await EditorApi.getFonts();
      this.availableFonts = response.fonts;
    }

    await this.loadWidgetProperties();
  }

  @Watch('selectedComponent.value')
  handler() {
    this.textValue = this.selectedComponent.value;
  }

  @Watch('textValue')
  onTextUpdate(_, oldValue) {
    const data = { value: this.textValue };

    if (!oldValue) return;

    this.onComponentUpdate({ ...this.selectedComponent, ...data });
  }

  get cssObject(): IComponentCss {
    return {
      color: this.color,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fontWeight: this.fontWeight,
      fontStyle: this.fontStyle,
      lineHeight: parseInt(this.lineHeight, 10) ? `${this.lineHeight}%` : 'normal',
      letterSpacing: parseInt(this.letterSpacing, 10) ? `${this.letterSpacing}px` : 'normal',
      textAlign: this.textAlign,
      textDecoration: this.textDecoration,
      textShadowColor: this.textShadowColor,
      textShadowOffset: this.textShadowOffset,
      textShadowEnabled: this.textShadowEnabled,
      textStrokeColor: this.textStrokeColor,
      textStrokeWidth: this.textStrokeWidth,
      textStrokeEnabled: this.textStrokeEnabled,
      tickerEnabled: this.tickerEnabled,
      backgroundColor: this.backgroundColor,
    };
  }

  get tabs(): ITextSettingsTab[] {
    return [
      { name: 'Simple', value: 'simple' },
      { name: 'Advanced', value: 'advanced' },
    ];
  }

  get availableFontOptions() {
    if (!this.availableFonts) return [];

    return Object.keys(this.availableFonts);
  }

  get availableFontWeightOptions(): IFormattedFontWeight {
    if (!this.availableFonts) return null;

    return this.availableFonts[this.fontFamily].map((value) => {
      return this.getFormattedFontWeight(value);
    });
  }

  get fontWeightOption(): IFormattedFontWeight {
    const formattedFontWeight = this.fontStyle === 'italic' ? 'italic' : '';

    return this.getFormattedFontWeight(`${this.fontWeight}${formattedFontWeight}`);
  }

  get textAlignValues() {
    return {
      left: `<i class='fa fa-align-left'></i>`,
      center: `<i class='fa fa-align-center'></i>`,
      right: `<i class='fa fa-align-right'></i>`,
    };
  }

  @Watch('selectedComponent.id')
  async loadWidgetProperties() {
    if (!this.selectedComponent) return;

    this.textValue = this.selectedComponent.value;

    this.shouldTriggerSave = false;
    await this.resetProperties(this.selectedComponent);
    this.shouldTriggerSave = true;
  }

  async resetProperties(selectedComponent = null) {
    const componentSettings = this.isVerticalEditor && selectedComponent.verticalSettings
      ? selectedComponent.verticalSettings
      : selectedComponent;

    this.fontFamily = componentSettings?.css?.fontFamily ?? DEFAULT_TEXT_SETTINGS.fontFamily;

    if (componentSettings.css?.fontFamily) {
      this.handleFontChange(this.fontFamily);
    }

    this.color = componentSettings?.css?.color ?? DEFAULT_TEXT_SETTINGS.color;
    this.fontSize = componentSettings?.css?.fontSize ?? DEFAULT_TEXT_SETTINGS.fontSize;
    this.fontWeight = componentSettings?.css?.fontWeight ?? DEFAULT_TEXT_SETTINGS.fontWeight;
    this.fontStyle = componentSettings?.css?.fontStyle ?? DEFAULT_TEXT_SETTINGS.fontStyle;
    this.lineHeight = componentSettings?.css?.lineHeight?.replace('%', '') ?? DEFAULT_TEXT_SETTINGS.lineHeight;
    this.letterSpacing = componentSettings?.css?.letterSpacing?.replace('px', '') ?? DEFAULT_TEXT_SETTINGS.letterSpacing;
    this.textAlign = componentSettings?.css?.textAlign ?? DEFAULT_TEXT_SETTINGS.textAlign;
    this.textDecoration = componentSettings?.css?.textDecoration ?? DEFAULT_TEXT_SETTINGS.textDecoration;
    this.textShadowEnabled = componentSettings?.css?.textShadowEnabled ?? DEFAULT_TEXT_SETTINGS.textShadowEnabled;
    this.textShadowColor = componentSettings?.css?.textShadowColor ?? DEFAULT_TEXT_SETTINGS.textShadowColor;
    this.textShadowOffset = componentSettings?.css?.textShadowOffset ?? DEFAULT_TEXT_SETTINGS.textShadowOffset;
    this.textStrokeEnabled = componentSettings?.css?.textStrokeEnabled ?? DEFAULT_TEXT_SETTINGS.textStrokeEnabled;
    this.textStrokeColor = componentSettings?.css?.textStrokeColor ?? DEFAULT_TEXT_SETTINGS.textStrokeColor;
    this.textStrokeWidth = componentSettings?.css?.textStrokeWidth ?? DEFAULT_TEXT_SETTINGS.textStrokeWidth;
    this.tickerEnabled = componentSettings?.css?.tickerEnabled ?? DEFAULT_TEXT_SETTINGS.tickerEnabled;
    this.backgroundColor = componentSettings?.css?.backgroundColor ?? DEFAULT_TEXT_SETTINGS.backgroundColor;
  }

  @Watch('color')
  @Watch('fontSize')
  @Watch('fontFamily')
  @Watch('fontWeight')
  @Watch('fontStyle')
  @Watch('lineHeight')
  @Watch('letterSpacing')
  @Watch('textAlign')
  @Watch('textDecoration')
  @Watch('textShadowEnabled')
  @Watch('textShadowColor')
  @Watch('textShadowOffset')
  @Watch('textStrokeEnabled')
  @Watch('textStrokeColor')
  @Watch('textStrokeWidth')
  @Watch('tickerEnabled')
  @Watch('backgroundColor')
  handleCSSChange(newValue, oldValue) {
    if (
      oldValue === null ||
      newValue === oldValue ||
      oldValue === DEFAULT_TEXT_SETTINGS.color
    ) return;

    let data: Partial<IComponent> = {};

    if (this.isVerticalEditor) {
      data.verticalSettings = {
        css: {
          ...this.selectedComponent.verticalSettings?.css || {},
          ...this.cssObject,
        },
      };
    } else {
      data.css = {
        ...this.selectedComponent.css || {},
        ...this.cssObject,
      };
    }

    this.onComponentUpdate({ ...this.selectedComponent, ...data }, this.shouldTriggerSave);
  }

  addStreamLabelFromModal(label) {
    if (!label) return;
    const value = `${this.selectedComponent.value} ${label}`;

    const data = {
      id: this.selectedComponent.id,
      value,
    };

    this.onComponentUpdate(data);
  }

  getFontWeights(font) {
    return [];
    // return Utilities.acceptableFontWeights(font, this.availableFonts);
  }

  async handleFontChange(selectedFont) {
    // reset font weight and style
    this.fontWeight = DEFAULT_TEXT_SETTINGS.fontWeight;
    this.fontStyle = DEFAULT_TEXT_SETTINGS.fontStyle;

    const fontWeights = this.getFontWeights(selectedFont);

    this.fontFamily = selectedFont;

    await FontHelper.loadWebFont({ font: selectedFont, weights: fontWeights });
  }

  handleFontWeightChange(option) {
    this.fontWeight = option.fontWeight;
    this.fontStyle = option.fontStyle ? option.fontStyle : 'normal';
  }

  onLineHeightBlur(event) {
    if (event) {
      const value = event.target.value;

      // reset to 'Normal' if value is not integer
      if (!parseInt(value, 10)) {
        this.lineHeight = DEFAULT_TEXT_SETTINGS.lineHeight;
        event.target.value = DEFAULT_TEXT_SETTINGS.lineHeight;
      }
    }
  }

  onLetterSpacingBlur(event) {
    if (event) {
      const value = event.target.value;

      // reset to 'Normal' if value is not integer
      if (!parseInt(value, 10)) {
        this.letterSpacing = DEFAULT_TEXT_SETTINGS.letterSpacing;
        event.target.value = DEFAULT_TEXT_SETTINGS.letterSpacing;
      }
    }
  }

  onTextShadowOffsetBlur(event) {
    if (event) {
      // regex for *px *px (*px)
      const offsetRegex = /(-?[0-9]+px\s?){2,3}/;
      const value = event.target.value;

      // reset to '0px 0px 0px' if value is not acceptable
      if (!offsetRegex.test(value)) {
        this.textShadowOffset = DEFAULT_TEXT_SETTINGS.textShadowOffset;
        event.target.value = DEFAULT_TEXT_SETTINGS.textShadowOffset;
      }
    }
  }

  getFormattedFontWeight(value): IFormattedFontWeight {
    switch (value) {
      case '100':
        return { id: value, label: 'Extra Light', fontWeight: 100 };
      case '100italic':
        return { id: value, label: 'Extra Light Italic', fontWeight: 100, fontStyle: 'italic' };
      case '200':
        return { id: value, label: 'Thin', fontWeight: 200 };
      case '200italic':
        return { id: value, label: 'Thin Italic', fontWeight: 300, fontStyle: 'italic' };
      case '300':
        return { id: value, label: 'Light', fontWeight: 300 };
      case '300italic':
        return { id: value, label: 'Light Italic', fontWeight: 300, fontStyle: 'italic' };
      case 'regular':
        return { id: value, label: 'Regular', fontWeight: 400 };
      case 'italic':
        return { id: value, label: 'Italic', fontWeight: 400, fontStyle: 'italic' };
      case '400':
        return { id: value, label: 'Regular', fontWeight: 400 };
      case '400italic':
        return { id: value, label: 'Italic', fontWeight: 400, fontStyle: 'italic' };
      case '500':
        return { id: value, label: 'Medium', fontWeight: 500 };
      case '500italic':
        return { id: value, label: 'Medium Italic', fontWeight: 500, fontStyle: 'italic' };
      case '600':
        return { id: value, label: 'Semi Bold', fontWeight: 600 };
      case '600italic':
        return { id: value, label: 'Semi Bold Italic', fontWeight: 600, fontStyle: 'italic' };
      case '700':
        return { id: value, label: 'Bold', fontWeight: 700 };
      case '700italic':
        return { id: value, label: 'Bold Italic', fontWeight: 700, fontStyle: 'italic' };
      case '800':
        return { id: value, label: 'Extra Bold', fontWeight: 800 };
      case '800italic':
        return { id: value, label: 'Extra Bold Italic', fontWeight: 800, fontStyle: 'italic' };
      case '900':
        return { id: value, label: 'Black', fontWeight: 900 };
      case '900italic':
        return { id: value, label: 'Black Italic', fontWeight: 900, fontStyle: 'italic' };
      default:
        return { id: value, label: value, fontWeight: value };
    }
  }

  toggleUnderline() {
    if (!this.textDecoration) {
      this.textDecoration = 'underline';
      return;
    }

    this.textDecoration = null;
  }

  updateColorValue(value) {
    this.color = value.hex;
  }

  async onInputBlur(_event, dataName: string, min: number, max: number) {
    if (!this[dataName]) return;

    if (this[dataName] > max) {
      await this.$nextTick(() => {
        this[dataName] = max;
      });
    }
    if (this[dataName] < min) {
      await this.$nextTick(() => {
        this[dataName] = min;
      });
    }
  }
}
